/**
 * custom.featured-collections.js
 *
 * Keeps the page dots of the "Featured collections" slider in sync with Horizon's stock
 * <slideshow-component> (assets/slideshow.js), which owns scrolling, arrows, drag and a11y.
 * With several cards per view the stock `current` index is a slide index, not a page, so this
 * wrapper maps it onto page dots. One dot set is rendered per breakpoint (data-visible = cards
 * per view there); CSS shows the matching set.
 */
class CustomFeaturedCollectionsSlider extends HTMLElement {
  /** @type {number} */
  #count = 0;

  connectedCallback() {
    this.#count = Number(this.dataset.count) || this.querySelectorAll('slideshow-slide').length;
    this.addEventListener('slideshow:select', this.#onSelect);
    this.#update(0);
  }

  disconnectedCallback() {
    this.removeEventListener('slideshow:select', this.#onSelect);
  }

  /** @param {Event & { detail?: { index?: number } }} event */
  #onSelect = (event) => {
    const index = event.detail?.index;
    if (typeof index === 'number') this.#update(index);
  };

  /**
   * Marks the dot for the page that contains slide `current` as current, in every dot set.
   * Page p starts at slide p * visible; the last page is pinned to the last possible start
   * (count - visible) because the scroller cannot scroll further than that.
   * @param {number} current - zero-based index of the slide the slideshow reports as current
   */
  #update(current) {
    for (const set of this.querySelectorAll('[data-cfc-dots]')) {
      const visible = Number(set.getAttribute('data-visible')) || 1;
      const dots = set.querySelectorAll('button');
      const last = dots.length - 1;
      if (last < 0) continue;

      const page = current >= this.#count - visible ? last : Math.min(last, Math.floor(current / visible));

      dots.forEach((dot, i) => dot.setAttribute('aria-current', i === page ? 'true' : 'false'));
    }
  }
}

if (!customElements.get('custom-featured-collections-slider')) {
  customElements.define('custom-featured-collections-slider', CustomFeaturedCollectionsSlider);
}
